// utils/inputValidation.ts
// Utility functions for input validation and basic SQL injection prevention

/**
 * Checks if a string contains common SQL injection patterns.
 * Returns true if input is safe, false if potentially dangerous.
 */
export function isSafeSqlString(input: string): boolean {
    if (!input) return true;
    // Common SQL injection patterns
    const patterns = [
        /('|"|;|--|#|\/\*|\*\/|\b(OR|AND|SELECT|INSERT|UPDATE|DELETE|DROP|UNION|EXEC|XP_)\b)/i,
        /\b(0x[0-9A-F]+)\b/i, // hex literals
        /\b(\d+\s*=\s*\d+)\b/i, // tautologies
    ];
    return !patterns.some(pattern => pattern.test(input));
}

/**
 * Validates an email address format.
 */
export function isValidEmail(email: string): boolean {
    return /^[\w.-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(email);
}

/**
 * Validates a username (alphanumeric, underscores, 3-32 chars).
 */
export function isValidUsername(username: string): boolean {
    return /^[A-Za-z0-9_]{3,32}$/.test(username);
}

/**
 * Validates a password (min 8 chars, at least 1 letter, 1 digit, 1 symbol;
 * only letters/digits/the listed symbols allowed). Mirrored on the backend.
 * Common-password and user-similarity rules still enforced server-side.
 */
export const PASSWORD_ALLOWED_SYMBOLS = "!@#$%^&*()_+-=";
export function isValidPassword(password: string): boolean {
    return /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=])[A-Za-z\d!@#$%^&*()_+\-=]{8,}$/.test(password);
}

/**
 * Validates a phone number (basic international, digits, +, -, spaces, 7-20 chars).
 */
export function isValidPhone(phone: string): boolean {
    return /^[+\d][\d\s-]{6,19}$/.test(phone);
}

/**
 * Validates a name (letters, spaces, hyphens, apostrophes, 2-64 chars).
 */
export function isValidName(name: string): boolean {
    return /^[A-Za-zÀ-ÿ'\- ]{1,64}$/.test(name.trim());
}

/**
 * Validates a numeric input (integer or float, optional min/max).
 */
export function isValidNumber(input: string, min?: number, max?: number): boolean {
    const num = Number(input);
    if (isNaN(num)) return false;
    if (min !== undefined && num < min) return false;
    if (max !== undefined && num > max) return false;
    return true;
}

/**
 * Escapes special characters for safe HTML rendering.
 */
export function escapeHtml(input: string): string {
    return input.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/**
 * Trims and removes dangerous SQL characters from input (for extra defense, not a replacement for parameterized queries).
 */
export function sanitizeSqlInput(input: string): string {
    return input.replace(/('|"|;|--|#|\/\*)/g, '').trim();
}

/**
 * Checks if a string is a valid UUID (v4).
 */
export function isValidUUID(uuid: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
}

/**
 * Checks if a string is a valid ISO date (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ssZ).
 */
export function isValidISODate(date: string): boolean {
    return /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?Z?)?$/.test(date);
}
